import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { getAuth } from 'firebase/auth';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { API_URL } from '../../constants/string-constants';
import { ModalWrapper, ModalContent, ModernTitle, ModernSubtitle } from './shared/ModalBase';

dayjs.extend(relativeTime);

const TRUST_BADGE_NAMES = ['Restricted', 'New', 'Member', 'Regular'];

const CloseIcon = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: 1.5rem;
  right: 1.5rem;
  transition: all 0.3s ease;
  color: var(--clr-sec);
  opacity: 0.7;
  z-index: 10;

  &:hover {
    color: #ff6b6b;
    opacity: 1;
    transform: scale(1.1);
  }
`;

const Scroller = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 0.5rem;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionHeading = styled.h3`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--clr-sec);
  opacity: 0.7;
  margin: 0 0 0.75rem 0;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
`;

const Stat = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--clr-sec);
  opacity: 0.6;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  font-size: 0.95rem;
  color: ${(props) => props.$color || 'var(--clr-sec)'};
  word-break: break-word;
`;

const FieldValue = styled.div`
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--clr-sec);
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const HistoryItem = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid ${(props) => props.$accent || 'rgba(255,255,255,0.2)'};
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: var(--clr-sec);
`;

const HistoryTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const HistoryReason = styled.span`
  font-weight: 600;
  word-break: break-word;
`;

const HistoryDate = styled.span`
  opacity: 0.6;
  font-size: 0.78rem;
  white-space: nowrap;
`;

const HistoryMeta = styled.div`
  opacity: 0.7;
  font-size: 0.78rem;
`;

const Quoted = styled.div`
  margin-top: 0.35rem;
  padding: 0.4rem 0.5rem;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  font-style: italic;
  opacity: 0.85;
  word-break: break-word;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${(props) => props.$bg || 'rgba(255,255,255,0.1)'};
  color: ${(props) => props.$fg || 'var(--clr-sec)'};
`;

const Empty = styled.div`
  color: var(--clr-sec);
  opacity: 0.55;
  font-style: italic;
  font-size: 0.85rem;
`;

const Centered = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 0.9rem;
  color: ${(props) => (props.$error ? '#ff6b6b' : 'var(--clr-sec)')};
  opacity: ${(props) => (props.$error ? 1 : 0.8)};
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: filter 0.2s ease;
  background: ${(props) => props.$bg};
  color: #fff;

  &:hover:not(:disabled) {
    filter: brightness(1.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ViewAccountModal = ({
  visible,
  onClose,
  userId,
  onBanUser,
  onSendFriendRequest,
  friendRequestSent,
  friendRequestLoading,
}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible && userId) {
      loadAccount();
    }
    if (!visible) {
      setData(null);
      setError(null);
    }
  }, [visible, userId]);

  const loadAccount = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        setError('Please sign in to view accounts');
        return;
      }
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/account/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setData(await response.json());
      } else {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Failed to load account');
      }
    } catch (err) {
      console.error('Error loading account:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const account = data?.account;
  const bans = data?.bans || [];
  const reports = data?.reports || [];
  const warnings = data?.warnings || [];
  const nicknameHistory = data?.nicknameHistory || [];

  return (
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        // Must clear the dashboard's full-screen overlay (z-index 2000);
        // matches ReportDetailsModal's 3000.
        overlay: { zIndex: 3000, backgroundColor: 'rgba(0,0,0,0.6)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseIcon onClick={onClose} />
          <ModernTitle style={{ color: account?.color || undefined }}>
            {account?.nickname || 'Account'}
          </ModernTitle>
          <ModernSubtitle>
            {account ? `${account.id.substring(0, 8)}… · Account view` : 'Account view'}
          </ModernSubtitle>

          {isLoading && <Centered>Loading account…</Centered>}
          {error && <Centered $error>{error}</Centered>}

          {account && !isLoading && (
            <Scroller>
              <Section>
                <SectionHeading>Status</SectionHeading>
                <StatGrid>
                  <Stat>
                    <StatLabel>Presence</StatLabel>
                    <StatValue $color={account.isOnline ? '#4ade80' : undefined}>
                      {account.isOnline
                        ? `Online · ${account.currentRoom || 'unknown'}`
                        : account.lastActiveAt
                        ? `Last seen ${dayjs(account.lastActiveAt).fromNow()}`
                        : 'Never active'}
                    </StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>Trust</StatLabel>
                    <StatValue>
                      {TRUST_BADGE_NAMES[account.trustLevel] || 'New'}
                      {account.trustManualOverride !== null && ' (override)'}
                    </StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>Account age</StatLabel>
                    <StatValue>
                      {account.accountAgeHours < 48
                        ? `${account.accountAgeHours}h`
                        : `${Math.floor(account.accountAgeHours / 24)}d`}
                    </StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>Clean messages</StatLabel>
                    <StatValue>{account.cleanMessageCount}</StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>Ban status</StatLabel>
                    <StatValue $color={account.currentlyBanned ? '#ff6b6b' : '#4ade80'}>
                      {account.currentlyBanned
                        ? `Banned until ${dayjs(account.unbanDate).format('MMM D, YYYY')}`
                        : 'Not banned'}
                    </StatValue>
                  </Stat>
                  {account.readOnlyUntil && new Date(account.readOnlyUntil) > new Date() && (
                    <Stat>
                      <StatLabel>Read-only</StatLabel>
                      <StatValue $color="#fbbf24">
                        until {dayjs(account.readOnlyUntil).format('MMM D, HH:mm')}
                      </StatValue>
                    </Stat>
                  )}
                </StatGrid>
              </Section>

              <Section>
                <SectionHeading>Profile</SectionHeading>
                <FieldValue>{account.bio || <Empty>No bio set</Empty>}</FieldValue>
                <StatGrid style={{ marginTop: '0.75rem' }}>
                  <Stat>
                    <StatLabel>Age</StatLabel>
                    <StatValue>
                      {account.age != null ? account.age : <Empty>{account.agePublic ? 'Not set' : 'Private'}</Empty>}
                    </StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>Pronouns</StatLabel>
                    <StatValue>
                      {account.pronouns || (
                        <Empty>{account.pronounsPublic ? 'Not set' : 'Private'}</Empty>
                      )}
                    </StatValue>
                  </Stat>
                </StatGrid>
              </Section>

              <Section>
                <SectionHeading>Past nicknames ({nicknameHistory.length})</SectionHeading>
                {nicknameHistory.length === 0 && <Empty>No nickname changes on record</Empty>}
                {nicknameHistory.map((n) => (
                  <HistoryItem key={n.id}>
                    <HistoryTop>
                      <HistoryReason>
                        {n.oldNickname || <Empty as="span">none</Empty>} → {n.newNickname}
                      </HistoryReason>
                      <HistoryDate>{dayjs(n.changedAt).format('MMM D, YYYY')}</HistoryDate>
                    </HistoryTop>
                  </HistoryItem>
                ))}
              </Section>

              <Section>
                <SectionHeading>Ban history ({bans.length})</SectionHeading>
                {bans.length === 0 && <Empty>No bans on record</Empty>}
                {bans.map((ban) => (
                  <HistoryItem key={ban.id} $accent={ban.isActive ? '#ff6b6b' : 'rgba(255,255,255,0.2)'}>
                    <HistoryTop>
                      <HistoryReason>{ban.reason || 'No reason given'}</HistoryReason>
                      <HistoryDate>{dayjs(ban.createdAt).format('MMM D, YYYY')}</HistoryDate>
                    </HistoryTop>
                    <HistoryMeta>
                      {ban.isActive ? (
                        <Pill $bg="rgba(255,107,107,0.2)" $fg="#ff6b6b">
                          ACTIVE
                        </Pill>
                      ) : (
                        <Pill>expired</Pill>
                      )}{' '}
                      {ban.byMod ? `by ${ban.byMod}` : ''}
                      {ban.unbanDate ? ` · until ${dayjs(ban.unbanDate).format('MMM D, YYYY')}` : ''}
                    </HistoryMeta>
                    {ban.bannedMessage && <Quoted>"{ban.bannedMessage}"</Quoted>}
                  </HistoryItem>
                ))}
              </Section>

              <Section>
                <SectionHeading>Reports against ({reports.length})</SectionHeading>
                {reports.length === 0 && <Empty>No reports filed against this user</Empty>}
                {reports.map((r) => (
                  <HistoryItem
                    key={r.id}
                    $accent={r.status === 'pending' ? '#fbbf24' : 'rgba(255,255,255,0.2)'}
                  >
                    <HistoryTop>
                      <HistoryReason>{r.reason}</HistoryReason>
                      <HistoryDate>{dayjs(r.createdAt).format('MMM D, YYYY')}</HistoryDate>
                    </HistoryTop>
                    <HistoryMeta>
                      <Pill
                        $bg={r.status === 'pending' ? 'rgba(251,191,36,0.2)' : undefined}
                        $fg={r.status === 'pending' ? '#fbbf24' : undefined}
                      >
                        {r.status}
                      </Pill>{' '}
                      in {r.room}
                      {r.resolvedAction ? ` · ${r.resolvedAction}` : ''}
                    </HistoryMeta>
                    {r.messageText && <Quoted>"{r.messageText}"</Quoted>}
                  </HistoryItem>
                ))}
              </Section>

              {warnings.length > 0 && (
                <Section>
                  <SectionHeading>Warnings ({warnings.length})</SectionHeading>
                  {warnings.map((w) => (
                    <HistoryItem key={w.id} $accent="#60a5fa">
                      <HistoryTop>
                        <HistoryReason>{w.reason}</HistoryReason>
                        <HistoryDate>{dayjs(w.createdAt).format('MMM D, YYYY')}</HistoryDate>
                      </HistoryTop>
                      <HistoryMeta>
                        {w.acknowledged ? <Pill>acknowledged</Pill> : <Pill>unread</Pill>}{' '}
                        in {w.room}
                        {w.byMod ? ` · by ${w.byMod}` : ''}
                      </HistoryMeta>
                    </HistoryItem>
                  ))}
                </Section>
              )}

              <Actions>
                <ActionButton
                  $bg="#4a90d9"
                  onClick={() => onSendFriendRequest?.(account.id)}
                  disabled={friendRequestLoading || friendRequestSent}
                >
                  {friendRequestSent ? 'Sent!' : friendRequestLoading ? 'Sending…' : 'Add Friend'}
                </ActionButton>
                <ActionButton
                  $bg="#d94a4a"
                  onClick={() => {
                    onBanUser?.(account.id, 0, account.nickname);
                    onClose();
                  }}
                >
                  Ban User
                </ActionButton>
              </Actions>
            </Scroller>
          )}
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default ViewAccountModal;
